import { Fragment, useEffect, useState } from "react";
import { FC } from "react";
import DatePicker from "react-datepicker";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import ClearDataButton from "../ClearDataButton";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import { changeDate } from "utils/changeDate";
import { useData } from "data/data-provider";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import { getUnavailableVehicleDataV2 } from "api/vehicles/vehicle";
import toast from "react-hot-toast";
import DatePickerCustomHeaderOneMonth from "components/DatePickerCustomHeaderOneMonth";
import moment from "moment";

interface UnavailabilityData {
  full_unavailable: string[];
  partial_unavailable: { [key: string]: [string, string][] };
}

export interface RentalCarDatesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  hasButtonSubmit?: boolean;
  selectedCar?: any;
  fleetId?: number;
  isMiddle?: boolean;
  searchBar?: boolean;
  minDays?: number | null | undefined;
  maxDays?: number | null | undefined;
}

const RentalCarDatesRangeInput: FC<RentalCarDatesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  selectedCar,
  fleetId,
  isMiddle,
  searchBar,
  minDays,
  maxDays,
}) => {
  const {
    startDate,
    startHours,
    startMinutes,
    startPeriod,
    endDate,
    endHours,
    endMinutes,
    endPeriod,
    setStartDate,
    setEndDate,
    setPartiallyAvailableStartDate,
    setPartiallyAvailableEndDate,
    setUnavailable,
    setPartiallyUnavailableTimes,
    pickupLocation,
  } = useData();

  const [fullyUnavailableDates, setFullyUnavailableDates] = useState<Date[]>(
    []
  );
  const [partialUnavailability, setPartialUnavailability] = useState<{
    [key: string]: { from: string; to: string }[];
  }>({});
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState<Date | null>(null);

  // Helper for formatting date to "YYYY-MM-DD"
  const formatDate = (date: Date) => moment(date).format("YYYY-MM-DD");

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const monthsToShow = isLargeScreen ? 2 : 1;

  const parsePartialUnavailability = (partialUnavailable: {
    [key: string]: [string, string][];
  }) => {
    const result: { [key: string]: { from: string; to: string }[] } = {};

    for (const [dateKey, timeRanges] of Object.entries(partialUnavailable)) {
      result[dateKey] = timeRanges.map(([fromTime, toTime]) => ({
        from: `${fromTime}:00`,
        to: `${toTime}:00`,
      }));
    }

    return result;
  };

  // ---- API CALL: fetch unavailable dates for current visible months ----
  const fetchUnavailableDates = async (startDate: Date, endDate: Date) => {
    if (!selectedCar?.id) {
      console.warn("No car selected, unable to fetch unavailable dates.");
      return;
    }

    setIsLoading(true);
    try {
      const startDateString = new Date(startDate);
      startDateString.setMonth(startDateString.getMonth() - 1);
      const endDateString = new Date(endDate);
      endDateString.setMonth(endDateString.getMonth() + 1);

      const startDateStr = formatDate(startDateString);
      const endDateStr = formatDate(endDateString);
      const response = await getUnavailableVehicleDataV2(
        {
          start_date: startDateStr,
          end_date: endDateStr,
        },
        selectedCar.id,
        pickupLocation?.id
      );

      if (response.data) {
        const unavailableData = response.data as UnavailabilityData;
        const fullyUnavailable = unavailableData.full_unavailable.map(
          (dateStr) => new Date(dateStr)
        );
        setFullyUnavailableDates(fullyUnavailable);
        const partialUnavailable = parsePartialUnavailability(
          unavailableData.partial_unavailable
        );
        setPartialUnavailability(partialUnavailable);
        if (setPartiallyUnavailableTimes) {
          setPartiallyUnavailableTimes(partialUnavailable as any);
        }
      }
    } catch (error) {
      console.error("Error fetching unavailable dates:", error);
      toast.error("Failed to load availability data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Check for fully unavailable dates in range ----
  const checkForUnavailableDatesInRange = (
    dates: [Date | null, Date | null]
  ) => {
    const selectedDates = dates.filter((date) => date !== null) as Date[];

    if (selectedDates.length < 2) {
      return false;
    }

    const [start, end] = selectedDates;
    const dateRange = [];
    let currentDate = new Date(start);
    while (currentDate <= end) {
      dateRange.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dateRange.some((date) =>
      fullyUnavailableDates.some(
        (unavailableDate) =>
          unavailableDate.toDateString() === date.toDateString()
      )
    );
  };

  // ---- Restrict Range by min/max days and block selection of unavailable dates ----
  const isDateSelectable = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Can't select fully unavailable
    const isFullyUnavailable = fullyUnavailableDates.some(
      (unavailableDate) =>
        unavailableDate.toDateString() === date.toDateString()
    );
    if (isFullyUnavailable) return false;

    // Can't select dates before today
    if (date < today) return false;

    // Enforce min/max booking rules if startDate selected
    if (startDate && minDays && maxDays) {
      // minEnd should be startDate + (minDays - 2)
      // maxEnd should be startDate + (maxDays - 1)
      const minEnd = new Date(startDate);
      minEnd.setDate(startDate.getDate() + minDays - 2);
      const maxEnd = new Date(startDate);
      maxEnd.setDate(startDate.getDate() + maxDays - 1);

      // If selecting end date
      if (date > startDate) {
        if (date < minEnd || date > maxEnd) return false;
      }
    }
    return true;
  };

  // ---- DatePicker onChange logic, ensures unavailable dates can't be selected ----
  const onChangeDate = (dates: [Date | null, Date | null]) => {
    if (dates[0] && dates[1]) {
      const [start, end] = dates;
      let currentDate = new Date(start);
      currentDate.setDate(currentDate.getDate() + 1); // Exclude start date
      let hasBlocked = false;
      while (currentDate < end) {
        const dateStr = formatDate(currentDate);
        const isFullyUnavailable = fullyUnavailableDates.some(
          (d) => d.toDateString() === currentDate.toDateString()
        );
        const isPartial = !!partialUnavailability[dateStr];
        if (isFullyUnavailable || isPartial) {
          hasBlocked = true;
          break;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      if (hasBlocked) {
        toast.error(
          "One or more days in your selected range are unavailable or partially unavailable."
        );
        changeDate(
          [null, null],
          startHours,
          startMinutes,
          startPeriod,
          endHours,
          endMinutes,
          endPeriod,
          setStartDate,
          setEndDate
        );
        setUnavailable(true);
        return;
      }
    }
    if (dates[0] && dates[1] && minDays) {
      const diffDays =
        Math.ceil(
          (dates[1].getTime() - dates[0].getTime()) / (1000 * 60 * 60 * 24)
        ) + 1;
      if (diffDays < minDays) {
        toast.error(`Minimum trip is ${minDays} days.`);
        changeDate(
          [null, null],
          startHours,
          startMinutes,
          startPeriod,
          endHours,
          endMinutes,
          endPeriod,
          setStartDate,
          setEndDate
        );
        setUnavailable(true);
        return;
      }
    }
    // Check for unavailable/partially unavailable days between start and minDays, excluding start and end dates
    if (dates[0] && !dates[1] && minDays && maxDays) {
      const start = dates[0];
      const minEnd = new Date(start);
      minEnd.setDate(start.getDate() + minDays - 1);

      let hasBlocked = false;
      let currentDate = new Date(start);
      currentDate.setDate(currentDate.getDate() + 1); // Start from the day after start date
      while (currentDate < minEnd) {
        // Exclude minEnd itself
        const dateStr = formatDate(currentDate);
        const isFullyUnavailable = fullyUnavailableDates.some(
          (d) => d.toDateString() === currentDate.toDateString()
        );
        const isPartial = !!partialUnavailability[dateStr];
        if (isFullyUnavailable || isPartial) {
          hasBlocked = true;
          break;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      if (hasBlocked) {
        changeDate(
          [null, null],
          startHours,
          startMinutes,
          startPeriod,
          endHours,
          endMinutes,
          endPeriod,
          setStartDate,
          setEndDate
        );
        toast.error(
          `Cannot start a trip here. The minimum trip is ${minDays} days and maximum is ${maxDays} days, but there are unavailable days in this range.`
        );
        setUnavailable(true);
        return;
      }
    }

    const hasUnavailableDates = checkForUnavailableDatesInRange(dates);
    if (selectedCar?.id) {
      if (hasUnavailableDates) {
        toast.error("This car is not available on one or more selected dates");
        setUnavailable(true);
        // Do not update dates if blocked
        return;
      } else {
        setUnavailable(false);
      }
    }
    changeDate(
      dates,
      startHours,
      startMinutes,
      startPeriod,
      endHours,
      endMinutes,
      endPeriod,
      setStartDate,
      setEndDate
    );

    // If startDate is partially available, set partial times (half hour later)
    if (dates[0]) {
      const startDateStr = formatDate(dates[0]);
      if (partialUnavailability[startDateStr]) {
        const partial = partialUnavailability[startDateStr][0];
        if (partial) {
          // Use the original date and set hours/minutes directly (local time, ignore timezone)
          const [hour, minute] = partial.from.split(":");
          const d = new Date(dates[0] as Date);
          d.setHours(Number(hour), Number(minute) + 30, 0, 0); // add 30 minutes
          setPartiallyAvailableStartDate(d);
          // Set hours/minutes/period using the original hour/minute values
          // const hourStr = String(
          //   Number(hour) + (Number(minute) + 30 >= 60 ? 1 : 0)
          // ).padStart(2, "0");
          // const minuteStr = String((Number(minute) + 30) % 60).padStart(2, "0");
          // setStartHours(hourStr);
          // setStartMinutes(minuteStr);
          // setStartPeriod(Number(hourStr) >= 12 ? "PM" : "AM");
        } else {
          setPartiallyAvailableStartDate(null);
        }
      } else {
        setPartiallyAvailableStartDate(null);
      }
    } else {
      setPartiallyAvailableStartDate(null);
    }

    // If endDate is partially available, set partial times (half hour earlier)
    if (dates[1]) {
      const endDateStr = formatDate(dates[1]);
      if (partialUnavailability[endDateStr]) {
        const partial = partialUnavailability[endDateStr][0];
        if (partial) {
          const [hour, minute] = partial.from.split(":");
          const d = new Date(dates[1] as Date);
          d.setHours(Number(hour), Number(minute) - 30, 0, 0); // subtract 30 minutes
          setPartiallyAvailableEndDate(d);
          // Set hours/minutes/period using the original hour/minute values
          // const hourStr = String(
          //   Number(hour) + (Number(minute) - 30 < 0 ? -1 : 0)
          // ).padStart(2, "0");
          // const minuteStr = String((Number(minute) - 30 + 60) % 60).padStart(
          //   2,
          //   "0"
          // );
          // setEndHours(hourStr);
          // setEndMinutes(minuteStr);
          // setEndPeriod(Number(hourStr) >= 12 ? "PM" : "AM");
        } else {
          setPartiallyAvailableEndDate(null);
        }
      } else {
        setPartiallyAvailableEndDate(null);
      }
    } else {
      setPartiallyAvailableEndDate(null);
    }

    // Fetch unavailability for new range (optional, for next steps)
    // Always fetch for the visible calendar range, not just the selected range
    // This ensures unavailable/partial data is always present for the calendar
    const today = new Date();
    const visibleEndDate = new Date(today);
    visibleEndDate.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, visibleEndDate);
  };

  // ---- Initial data fetch for visible months ----
  useEffect(() => {
    const today = new Date();
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, threeMonthsLater);
  }, [selectedCar?.id, monthsToShow, pickupLocation]);

  // ---- Month change handler for DatePicker ----
  const handleMonthChange = (date: Date) => {
    setCalendarViewDate(date);
    const viewStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const viewEndDate = new Date(
      date.getFullYear(),
      date.getMonth() + monthsToShow,
      0
    );
    fetchUnavailableDates(viewStartDate, viewEndDate);
  };

  // ---- Render Input Field ----
  const renderInput = () => {
    return (
      <>
        <div className="text-text-color">
          <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow text-left">
          <span
            className={`block xl:text-lg font-semibold ${
              searchBar === false ? "text-text-color" : "text-text-color"
            }`}
          >
            {startDate?.toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
            }) || "Add dates"}
            {endDate
              ? " - " +
                endDate?.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                })
              : ""}
          </span>
          <span
            className={`block mt-1 text-sm ${
              searchBar === false ? "text-text-color" : "text-text-color"
            } leading-none font-light`}
          >
            {"Pickup & Drop off Date"}
          </span>
        </div>
      </>
    );
  };

  // ---- Custom Day Rendering including unavailable/partial unavailable visual cues ----
  const renderDayContents = (day: number, date?: Date) => {
    if (!date) return day;
    const dateStr = formatDate(date);
    const isFullyUnavailable = fullyUnavailableDates.some(
      (d) => d.toDateString() === date.toDateString()
    );
    const isPartial = !!partialUnavailability[dateStr];

    // Custom visual: green line for available, half green/red for partial, red with strikethrough for blocked
    // You can enhance DatePickerCustomDay with these props for exact fleetwire-style visuals!
    return (
      <DatePickerCustomDay
        dayOfMonth={day}
        date={date}
        unavailableDates={fullyUnavailableDates}
        partiallyUnavailable={isPartial}
        partialTimes={partialUnavailability[dateStr]}
        // Add any extra styling/tooltip logic here as needed
      />
    );
  };

  return (
    <>
      <Popover
        className={`RentalCarDatesRangeInput z-20 relative flex ${className}`}
      >
        {({ open }) => (
          <>
            <div
              className={`flex-1 z-10 flex items-center focus:outline-none ${
                open ? "nc-hero-field-focused !rounded-md" : ""
              }`}
            >
              <PopoverButton
                className={`flex-1 z-10 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none `}
                onClickCapture={() => document.querySelector("html")?.click()}
              >
                {renderInput()}

                {startDate && open && (
                  <ClearDataButton
                    onClick={() => {
                      // const today = new Date();
                      // const endDate = new Date(today);
                      // endDate.setDate(today.getDate() + (minDays ?? 0));
                      // onChangeDate([null, null]);
                      setStartDate(null);
                      setEndDate(null);
                      setPartiallyAvailableStartDate(null);
                      setPartiallyAvailableEndDate(null);
                    }}
                  />
                )}
              </PopoverButton>
            </div>

            {open && (
              <div className="h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 -left-0.5 right-1 "></div>
            )}

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel
                className={`absolute left-1/2 z-40 mt-3 top-full w-screen max-w-2xl -translate-x-1/2 ${
                  isMiddle ? "lg:-translate-x-1/3" : "-translate-x-1/2"
                } xl:-translate-x-1/2 transform px-4 sm:px-0 lg:max-w-3xl`}
              >
                <div className="overflow-hidden rounded-md shadow-lg ring-1 ring-black ring-opacity-5 bg-secondary-50 p-8">
                  {isLoading ? (
                    <div className="pointer-events-none opacity-60 select-none">
                      <div className="relative">
                        {/* Calendar is visible but faded and unclickable */}
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                              style={{ background: "green" }}
                            />
                            <span className="text-xs text-text-color">
                              Available
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                              style={{
                                background:
                                  "linear-gradient(to right, red 50%, green 50%)",
                              }}
                            />
                            <span className="text-xs text-text-color">
                              Partially Available
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-6 h-1 rounded border-[0.5px] border-gray-300 line-through"
                              style={{ background: "red" }}
                            />
                            <span className="text-xs text-text-color">
                              Unavailable
                            </span>
                          </div>
                        </div>
                        <DatePicker
                          selected={startDate}
                          onChange={onChangeDate}
                          startDate={startDate}
                          endDate={endDate}
                          selectsRange
                          monthsShown={monthsToShow}
                          showPopperArrow={false}
                          inline
                          minDate={new Date()}
                          openToDate={
                            calendarViewDate || startDate || new Date()
                          }
                          filterDate={isDateSelectable}
                          renderCustomHeader={(p) => (
                            <>
                              <div className="block sm:hidden text-text-color">
                                <DatePickerCustomHeaderOneMonth {...p} />
                              </div>
                              <div className="hidden sm:block text-text-color">
                                <DatePickerCustomHeaderTwoMonth {...p} />
                              </div>
                            </>
                          )}
                          renderDayContents={renderDayContents}
                          onMonthChange={handleMonthChange}
                        />
                      </div>

                      <div className="absolute left-0 bottom-0 w-full px-8">
                        <div className="h-1 bg-gray-100 rounded-full overflow-hidden relative">
                          <div className="bar-loader h-full bg-blue-400 rounded-full absolute"></div>
                        </div>
                      </div>

                      <div className="absolute inset-0 bg-white opacity-0 pointer-events-auto" />
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="flex items-center space-x-1">
                          <div
                            className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                            style={{ background: "green" }}
                          />
                          <span className="text-xs text-text-color">
                            Available
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div
                            className="w-6 h-1 rounded border-[0.5px] border-gray-300"
                            style={{
                              background:
                                "linear-gradient(to right, red 50%, green 50%)",
                            }}
                          />
                          <span className="text-xs text-text-color">
                            Partially Available
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div
                            className="w-6 h-1 rounded border-[0.5px] border-gray-300 line-through"
                            style={{ background: "red" }}
                          />
                          <span className="text-xs text-text-color">
                            Unavailable
                          </span>
                        </div>
                      </div>
                      <DatePicker
                        selected={startDate}
                        onChange={onChangeDate}
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange
                        monthsShown={monthsToShow}
                        showPopperArrow={false}
                        inline
                        minDate={new Date()}
                        openToDate={calendarViewDate || startDate || new Date()}
                        filterDate={isDateSelectable}
                        renderCustomHeader={(p) => (
                          <>
                            <div className="block sm:hidden text-text-color">
                              <DatePickerCustomHeaderOneMonth {...p} />
                            </div>
                            <div className="hidden sm:block text-text-color">
                              <DatePickerCustomHeaderTwoMonth {...p} />
                            </div>
                          </>
                        )}
                        renderDayContents={renderDayContents}
                        onMonthChange={handleMonthChange}
                      />
                    </div>
                  )}
                    <Popover.Button
                    className="mt-6 w-full bg-primary-100 hover:bg-primary-200 text-[#121212] font-semibold rounded-lg py-3 transition-all flex items-center justify-center space-x-2"
                    onClick={() => {
                      
                    }}
                  >
                    <span className="text-white">Save Dates</span>
                   
                  </Popover.Button>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </>
  );
};
export default RentalCarDatesRangeInput;
